<template>
    <div>
    <v-simple-table>
        <tbody>
            <tr>
                <td><strong>{{ $t('cliente.labels.razonSocial') }}</strong></td>
                <td>{{ item.razonSocial }}</td>
            </tr>
            <tr>
                <td><strong>{{ $t('cliente.labels.tipoDocumento') }}</strong></td>
                <td>{{ item.tipoDocumento }}</td>
            </tr>
            <tr>
                <td><strong>{{ $t('cliente.labels.numeroDocumento') }}</strong></td>
                <td>{{ item.numeroDocumento }}</td>
            </tr>
            <tr>
                <td><strong>{{ $t('cliente.labels.condicionIVA') }}</strong></td>
                <td>{{ condicionIVALabel(item.condicionIVA) }}</td>
            </tr>
            <tr v-if="item.email">
                <td><strong>{{ $t('cliente.labels.email') }}</strong></td>
                <td>{{ item.email }}</td>
            </tr>
            <tr v-if="item.telefono">
                <td><strong>{{ $t('cliente.labels.telefono') }}</strong></td>
                <td>{{ item.telefono }}</td>
            </tr>
            <tr v-if="item.domicilio">
                <td><strong>{{ $t('cliente.labels.domicilio') }}</strong></td>
                <td>{{ item.domicilio }}</td>
            </tr>
            <tr v-if="item.piso">
                <td><strong>{{ $t('cliente.labels.piso') }}</strong></td>
                <td>{{ item.piso }}</td>
            </tr>
            <tr v-if="item.departamento">
                <td><strong>{{ $t('cliente.labels.departamento') }}</strong></td>
                <td>{{ item.departamento }}</td>
            </tr>
            <tr v-if="item.codigoPostal">
                <td><strong>{{ $t('cliente.labels.codigoPostal') }}</strong></td>
                <td>{{ item.codigoPostal }}</td>
            </tr>
            <tr>
                <td><strong>{{ $t('cliente.labels.activo') }}</strong></td>
                <td>
                    <v-chip :color="item.activo ? 'success' : 'error'" small>
                        {{ item.activo ? $t('common.active') : $t('common.inactive') }}
                    </v-chip>
                </td>
            </tr>
        </tbody>
    </v-simple-table>
    <v-divider class="my-3" />
    <div class="text-subtitle-1 mb-2">Cuenta corriente</div>
    <p v-if="loadingDeuda">Cargando...</p>
    <template v-else>
        <p><strong>Deuda actual:</strong> ${{ Number(deuda || 0).toFixed(2) }}</p>
        <div class="text-caption mb-1">Últimos movimientos</div>
        <v-simple-table v-if="movimientos.length" dense>
            <thead>
                <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Descripción</th>
                    <th>Monto</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="m in movimientos" :key="m.id">
                    <td>{{ formatFecha(m.fecha) }}</td>
                    <td>{{ labelTipo(m.tipo) }}</td>
                    <td>{{ descripcionMovimiento(m) }}</td>
                    <td :class="montoDisplay(m.monto).esPago ? 'success--text' : 'error--text'">{{ montoDisplay(m.monto).texto }}</td>
                </tr>
            </tbody>
        </v-simple-table>
        <p v-else class="text-caption">Sin movimientos.</p>
    </template>
    </div>
</template>

<script>
import CuentaClienteProvider from "../../../../ventas/providers/CuentaClienteProvider";
import { formatFechaVentas } from "../../../../ventas/utils/dateFormat";

const TIPO_LABELS = {
    DEUDA_PEDIDO: "Deuda Pedido",
    PAGO_PARCIAL: "Pago Parcial",
    PAGO_TOTAL: "Pago Total",
};

const CONDICION_IVA_LABELS = {
    CONSUMIDOR_FINAL: "Consumidor Final",
    RESPONSABLE_INSCRIPTO: "Responsable Inscripto",
    EXENTO: "Exento (IVA)",
    MONOTRIBUTISTA: "Monotributista",
    MONOTRIBUTO: "Monotributista",
    SUJETO_NO_CATEGORIZADO: "No responsable / Sujeto no categorizado",
};

export default {
    name: "ClienteShowData",
    props: {
        item: { type: Object, required: true }
    },
    data() {
        return {
            deuda: null,
            movimientos: [],
            loadingDeuda: false,
        };
    },
    computed: {
        clienteId() {
            return (this.item && (this.item.id || this.item._id)) || null;
        },
    },
    watch: {
        clienteId(id) {
            if (id) this.loadCuenta();
            else {
                this.deuda = null;
                this.movimientos = [];
            }
        },
    },
    mounted() {
        if (this.clienteId) this.loadCuenta();
    },
    methods: {
        formatFecha(val) {
            return formatFechaVentas(val);
        },
        labelTipo(tipo) {
            return (tipo && TIPO_LABELS[tipo]) || (tipo || "—").toLowerCase();
        },
        condicionIVALabel(value) {
            if (!value) return "—";
            return CONDICION_IVA_LABELS[value] || value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        },
        descripcionMovimiento(m) {
            if (m.pedidoId) {
                const id = String(m.pedidoId);
                return id.length >= 6 ? `Pedido #${id.slice(-6)}` : `Pedido #${id}`;
            }
            if (m.descripcion) return m.descripcion;
            return "—";
        },
        /** Cuando el cliente compra (deuda) mostramos negativo; cuando paga, positivo. */
        montoDisplay(monto) {
            const num = Number(monto);
            const invertido = -num;
            const esPago = invertido > 0;
            const signo = invertido > 0 ? "+" : "-";
            return {
                texto: `${signo}$${Math.abs(invertido).toFixed(2)}`,
                esPago,
            };
        },
        async loadCuenta() {
            const id = this.clienteId;
            if (!id) return;
            this.loadingDeuda = true;
            try {
                const [rDeuda, rMov] = await Promise.all([
                    CuentaClienteProvider.getDeuda(id),
                    CuentaClienteProvider.getMovimientos(id, 1, 10),
                ]);
                const rawDeuda = rDeuda?.data?.cuentaClienteDeuda;
                this.deuda = (rawDeuda != null && rawDeuda !== undefined) ? Number(rawDeuda) : 0;
                this.movimientos = (rMov?.data?.cuentaClienteMovimientos?.items) || [];
            } catch (e) {
                console.error(e);
            } finally {
                this.loadingDeuda = false;
            }
        },
    },
};
</script>

<style scoped>

</style>
